import React, { useCallback, useEffect, useState, ChangeEvent } from 'react';
import {
  Badge,
  Breadcrumbs,
  Button,
  Checkbox,
  Dialog,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DateRangePickerDay, { DateRangePickerDayProps } from '@mui/lab/DateRangePickerDay';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import StaticDateRangePicker from '@mui/lab/StaticDateRangePicker';
import { CommonLayout, CountryCodesDropDown } from './commonComponents';
import { ListPaginationBar } from './common/ListPagination';
import { MFSubmitButton, MFTextField } from '../lib/formik';
import MFSelectField from '../lib/formik/SelectField';
import { showError } from '../redux-store/actions/auth';
import {
  adminSupportMaster,
  ENABLE_PROCESS_SIGNING_APPLICATIONS,
  SUPPORT_STATUS,
} from '../utils/constant';
import { adminSupportValidation } from '../utils/schema';
import { preventSpecialCharacters } from '../utils/utilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../redux-store/reducers';
import {
  unlockUser,
  checkWhitelistUser,
  whitelistUser,
  checkWhitelistedResponceBody,
  supportTypeBody,
  unlockAccess,
  WhitelistingAccess,
  unlockAccessBody,
  whitelistAccessBody,
  changeStatusAccess,
  statusAccessBody,
  changeStatusToDraft,
  changeStatusFromExpiredexport,
  processSignedApplications,
  getAdminSupportLog,
  AdminSupportLog,
  GetAdminSupportLogResponse,
} from '../redux-store/actions/adminSupport';
import { RequiredFieldsMessage } from './RequiredFieldsMessage';
import { Link as RouterLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SearchSharpIcon from '@mui/icons-material/SearchSharp';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { DataTable, ColumnType } from './DataTable';
import {
  userMgmtBoxStyles,
  userMgmtRowStyles,
  userMgmtTableBodyStyles,
  userMgmtTableHeaderStyles,
} from './userManagement/userMgmtListStyles';
import { useDebounce } from '../utils/useDebounce';
import { formatShortDate } from '../utils/date';

export interface adminSupportValues {
  supportType: string;
  phone: string;
  countryCode: string;
  application_number: string;
  status: string;
}

const adminSupportInitialValues: adminSupportValues = {
  supportType: '',
  phone: '',
  countryCode: 'India: +91',
  status: '',
  application_number: '',
};

function formatTicketTime(date?: string | Date | null): string {
  if (!date) return 'N/A';
  return new Date(date).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

function formatSupportType(supportType: string): string {
  return adminSupportMaster[supportType] || supportType || 'N/A';
}

function formatMobile(row: AdminSupportLog): string {
  if (row.mobileNumber) {
    return row.countryCode ? `${row.countryCode}-${row.mobileNumber}` : row.mobileNumber;
  }
  return 'N/A';
}

export type AdminSupportFilters = {
  supportTypes: string[];
  from: string | null;
  to: string | null;
};

const initialAdminSupportFilters: AdminSupportFilters = {
  supportTypes: [],
  from: null,
  to: null,
};

const filterCheckboxSx = {
  ml: 0,
  mb: 0.5,
  mr: 0,
  width: '100%',
  alignItems: 'center',
  '.MuiTypography-root': {
    fontFamily: 'Inter, sans-serif',
    color: '#111927',
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1,
    letterSpacing: 0,
  },
  '.MuiCheckbox-root': { p: 0.5, mr: 1, color: '#9CA3AF' },
  '.Mui-checked': { color: 'primary.main' },
};

const RangeDay = (props: DateRangePickerDayProps<Date>) => (
  <DateRangePickerDay
    {...props}
    sx={{
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlight': {
        backgroundColor: '#E8ECF0',
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart, &.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd':
        {
          backgroundColor: 'transparent',
        },
      '& .MuiDateRangePickerDay-day': {
        fontSize: 13,
        fontWeight: 500,
        color: '#5F6B7A',
        borderRadius: '4px',
        '&.Mui-selected': {
          backgroundColor: '#2057A6',
          color: '#FFFFFF',
          fontWeight: 600,
          '&:hover, &:focus': {
            backgroundColor: '#2057A6',
          },
        },
        '&.MuiDateRangePickerDay-dayInsideRangeInterval': {
          color: '#293139',
          fontWeight: 600,
        },
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart .MuiDateRangePickerDay-day': {
        borderTopLeftRadius: '4px',
        borderBottomLeftRadius: '4px',
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day': {
        borderTopRightRadius: '4px',
        borderBottomRightRadius: '4px',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
      '&.MuiDateRangePickerDay-rangeIntervalDayHighlightStart.MuiDateRangePickerDay-rangeIntervalDayHighlightEnd .MuiDateRangePickerDay-day':
        {
          borderRadius: '4px',
        },
    }}
  />
);

function AdminSupportFilterDrawer({
  filters,
  open,
  onFiltersChange,
  handleClose,
}: {
  filters: AdminSupportFilters;
  open: boolean;
  onFiltersChange: (value: AdminSupportFilters) => void;
  handleClose: () => void;
}): JSX.Element {
  const [modalState, setModalState] = useState<AdminSupportFilters>(filters);
  const [activeCategory, setActiveCategory] = useState<'Support Type' | 'Date Range'>('Support Type');

  useEffect(() => {
    setModalState(filters);
    setActiveCategory('Support Type');
  }, [filters, open]);

  const closeWithoutApply = () => {
    handleClose();
    setModalState(filters);
  };

  const handleApply = () => {
    onFiltersChange(modalState);
    handleClose();
  };

  const handleClear = () => {
    onFiltersChange(initialAdminSupportFilters);
    handleClose();
  };

  const toggleSupportType = (key: string) => {
    setModalState((prev) => ({
      ...prev,
      supportTypes: prev.supportTypes.includes(key)
        ? prev.supportTypes.filter((item) => item !== key)
        : [...prev.supportTypes, key],
    }));
  };

  const renderCategoryOptions = () => {
    if (activeCategory === 'Support Type') {
      return (
        <RadioGroup aria-label="Support Type" name="supportType">
          {Object.keys(adminSupportMaster).map((key) => (
            <FormControlLabel
              key={key}
              value={key}
              onChange={() => toggleSupportType(key)}
              control={<Checkbox size="small" checked={modalState.supportTypes.includes(key)} />}
              label={adminSupportMaster[key]}
              title={adminSupportMaster[key]}
              sx={filterCheckboxSx}
            />
          ))}
        </RadioGroup>
      );
    }

    const fromDate = modalState.from ? new Date(modalState.from) : null;
    const toDate = modalState.to ? new Date(modalState.to) : null;
    const dateLabel =
      fromDate || toDate
        ? `${fromDate ? formatShortDate(fromDate) : 'From'} - ${
            toDate ? formatShortDate(toDate) : 'To'
          }`
        : 'Select date range';

    return (
      <Box>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: 14,
            fontWeight: 500,
            color: fromDate || toDate ? '#111927' : '#6B7280',
            mb: 1.5,
          }}>
          {dateLabel}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box
            sx={{
              '& .MuiPickerStaticWrapper-root': {
                minWidth: 'unset',
              },
              '& .MuiCalendarPicker-root': {
                width: '100%',
                maxHeight: 'unset',
              },
              '& .MuiPickersCalendarHeader-root': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiPickersCalendarHeader-label': {
                fontSize: 15,
                fontWeight: 700,
                color: '#293139',
              },
              '& .MuiDayPicker-header': {
                justifyContent: 'space-between',
              },
              '& .MuiDayPicker-weekDayLabel': {
                fontSize: 12,
                color: '#9AA3AF',
                fontWeight: 500,
                width: 36,
                height: 32,
              },
              '& .MuiDayPicker-weekContainer': {
                justifyContent: 'space-between',
                margin: 0,
              },
              '& .MuiDayPicker-slideTransition, & .MuiPickersSlideTransition-root, & .PrivatePickersSlideTransition-root':
                {
                  minHeight: '240px !important',
                },
              '& .MuiDateRangePickerDay-root': {
                width: 36,
                height: 36,
                margin: 0,
              },
            }}>
            <StaticDateRangePicker
              displayStaticWrapperAs="desktop"
              calendars={1}
              allowSameDateSelection
              value={[fromDate, toDate]}
              maxDate={new Date()}
              onChange={(newValue) => {
                const [start, end] = newValue;
                let from: string | null = null;
                let to: string | null = null;
                if (start) {
                  const startOfDay = new Date(start);
                  startOfDay.setHours(0, 0, 0, 0);
                  from = startOfDay.toISOString();
                }
                if (end) {
                  const endOfDay = new Date(end);
                  endOfDay.setHours(23, 59, 59, 999);
                  to = endOfDay.toISOString();
                }
                setModalState({ ...modalState, from, to });
              }}
              renderDay={(_day, dateRangePickerDayProps) => (
                <RangeDay {...dateRangePickerDayProps} />
              )}
              renderInput={(startProps, endProps) => (
                <>
                  <TextField {...startProps} sx={{ display: 'none' }} />
                  <TextField {...endProps} sx={{ display: 'none' }} />
                </>
              )}
            />
          </Box>
        </LocalizationProvider>
        {(fromDate || toDate) && (
          <Button
            type="button"
            onClick={() => setModalState({ ...modalState, from: null, to: null })}
            sx={{
              mt: 0.5,
              color: 'primary.main',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: 14,
              textTransform: 'none',
              p: 0,
              minWidth: 'unset',
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
            }}>
            Clear dates
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={closeWithoutApply}
      ModalProps={{
        id: 'filter-menu',
        role: 'dialog',
        'aria-label': 'Filter',
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 520 },
          maxWidth: '100%',
          borderRadius: { xs: 0, sm: '16px 0 0 16px' },
          fontFamily: 'Inter, sans-serif',
        },
      }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: '#FFFFFF',
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: '1px solid #E5E7EB',
          }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 18,
              fontWeight: 700,
              color: '#111927',
              lineHeight: 1,
            }}>
            Filter
          </Typography>
          <IconButton aria-label="Close" onClick={closeWithoutApply} sx={{ p: 0.5 }}>
            <CloseIcon sx={{ color: '#6B7280', fontSize: 22 }} aria-hidden="true" />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box
            component="nav"
            aria-label="Filter categories"
            sx={{
              width: 180,
              flexShrink: 0,
              borderRight: '1px solid #E5E7EB',
              py: 1,
              overflowY: 'auto',
            }}>
            {(['Support Type', 'Date Range'] as const).map((category) => {
              const isActive = activeCategory === category;
              return (
                <Box
                  key={category}
                  component="button"
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  aria-current={isActive ? 'true' : undefined}
                  sx={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    font: 'inherit',
                    position: 'relative',
                    px: 2.5,
                    py: 1.5,
                    color: isActive ? 'primary.main' : '#111927',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 16,
                    fontWeight: isActive ? 600 : 500,
                    lineHeight: 1,
                    '&::before': isActive
                      ? {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 8,
                          bottom: 8,
                          width: 3,
                          borderRadius: '0 2px 2px 0',
                          bgcolor: 'primary.main',
                        }
                      : undefined,
                    '&:hover': { bgcolor: '#F9FAFB' },
                    '&:focus-visible': {
                      outline: '3px solid #2057A6',
                      outlineOffset: '-3px',
                    },
                  }}>
                  {category}
                </Box>
              );
            })}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, p: 2.5, overflowY: 'auto' }}>{renderCategoryOptions()}</Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderTop: '1px solid #E5E7EB',
          }}>
          <Button
            type="reset"
            onClick={handleClear}
            sx={{
              color: 'primary.main',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: 14,
              lineHeight: 1,
              textTransform: 'none',
              p: 0,
              minWidth: 'unset',
              '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
            }}>
            Clear filters
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={handleApply}
            sx={{
              bgcolor: 'primary.main',
              color: '#FFFFFF',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              lineHeight: 1,
              textTransform: 'none',
              borderRadius: '8px',
              pt: '9px',
              pr: '28px',
              pb: '9px',
              pl: '28px',
              boxShadow: 'none',
              '&:hover': { bgcolor: 'primary.dark', boxShadow: 'none' },
            }}>
            Apply
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

export default function adminSupport(): JSX.Element {
  const dispatch = useDispatch();
  const [unlockAcc, setUnlockAcc] = useState(false);
  const [whitelistAccess, setWhitelistAccess] = useState(false);
  const [statusAccess, setStatusAccess] = useState(false);
  const { adminSupport } = useSelector((store: RootStateType) => store.auth);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [logs, setLogs] = useState<AdminSupportLog[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debounceSearch = useDebounce(search, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [noOfPages, setNoOfPages] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<AdminSupportFilters>(initialAdminSupportFilters);

  const fetchLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: {
        page: number;
        limit: number;
        search?: string;
        supportType?: string;
        from?: string;
        to?: string;
      } = {
        page: currentPage,
        limit: rowsPerPage,
      };
      const q = debounceSearch.trim();
      if (q) params.search = q;
      if (filters.supportTypes.length) params.supportType = filters.supportTypes.join(',');
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      const response = (await dispatch(
        getAdminSupportLog(params)
      )) as unknown as GetAdminSupportLogResponse;
      setLogs(response?.data || []);
      setTotal(response?.total || 0);
      setNoOfPages(Math.max(1, response?.pages || 1));
    } catch (e) {
      console.error((e as Error).message);
      setLogs([]);
      setTotal(0);
      setNoOfPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, currentPage, rowsPerPage, debounceSearch, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    (async function () {
      try {
        if (adminSupport) {
          const unlockAccessResponse = (await dispatch(
            unlockAccess()
          )) as unknown as unlockAccessBody;
          setUnlockAcc(unlockAccessResponse.unlockingAccess);
          const whitelistAccessResponse = (await dispatch(
            WhitelistingAccess()
          )) as unknown as whitelistAccessBody;
          setWhitelistAccess(whitelistAccessResponse.whitelistingAccess);
          const statusAccessResponse = (await dispatch(
            changeStatusAccess()
          )) as unknown as statusAccessBody;
          setStatusAccess(statusAccessResponse.applicationStatusChangeAccess);
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
  }, []);

  const supportOptions = Object.keys(adminSupportMaster).filter((item) => {
    if (unlockAcc && adminSupportMaster[item] === adminSupportMaster.unlock_user) {
      return item;
    }
    if (
      whitelistAccess &&
      [
        adminSupportMaster.check_whitelisted,
        adminSupportMaster.whitelist_user,
        adminSupportMaster.unwhitelist_user,
      ].includes(adminSupportMaster[item])
    ) {
      return item;
    }
    if (
      statusAccess &&
      [
        adminSupportMaster.change_expired_status,
        adminSupportMaster.change_status_to_draft,
      ].includes(adminSupportMaster[item])
    ) {
      return item;
    }
    if (
      ENABLE_PROCESS_SIGNING_APPLICATIONS &&
      adminSupportMaster[item] === adminSupportMaster.process_signed_applications
    ) {
      return item;
    }
    return;
  });

  const handleSubmit = async (values: adminSupportValues) => {
    try {
      if (adminSupportMaster[values.supportType] === adminSupportMaster.unlock_user) {
        const unlockResponse = (await dispatch(
          unlockUser({ phone: values.phone, countryCode: values.countryCode?.split(':')[1].trim() })
        )) as unknown as supportTypeBody;
        throw `${unlockResponse.message}`;
      }
      if (adminSupportMaster[values.supportType] === adminSupportMaster.check_whitelisted) {
        const checkWhitelistResponse = (await dispatch(
          checkWhitelistUser({
            phone: values.phone,
            countryCode: values.countryCode?.split(':')[1].trim(),
          })
        )) as unknown as checkWhitelistedResponceBody;
        throw `${
          checkWhitelistResponse.isMobileNumberWhiteListed
            ? 'This Number is Whitelisted'
            : 'This Number is not Whitelisted'
        }`;
      }
      if (adminSupportMaster[values.supportType] === adminSupportMaster.change_expired_status) {
        const changeStatusFromExpired = (await dispatch(
          changeStatusFromExpiredexport({
            applicationStatus: values.status,
            applicationNumber: values.application_number,
          })
        )) as unknown as supportTypeBody;
        throw `${changeStatusFromExpired.message}`;
      }
      if (adminSupportMaster[values.supportType] === adminSupportMaster.change_status_to_draft) {
        const changeStatusToDraftRes = (await dispatch(
          changeStatusToDraft({
            applicationNumber: values.application_number,
            deleteLeegalityDocument: false,
          })
        )) as unknown as supportTypeBody;
        throw `${changeStatusToDraftRes.message}`;
      }
      if (
        [adminSupportMaster.whitelist_user, adminSupportMaster.unwhitelist_user].includes(
          adminSupportMaster[values.supportType]
        )
      ) {
        const whitelistResponse = (await dispatch(
          whitelistUser({
            phone: values.phone,
            countryCode: values.countryCode?.split(':')[1].trim(),
            whiteList:
              adminSupportMaster[values.supportType] === adminSupportMaster.whitelist_user
                ? true
                : false,
          })
        )) as unknown as supportTypeBody;
        throw `${whitelistResponse.message}`;
      }
      if (
        ENABLE_PROCESS_SIGNING_APPLICATIONS &&
        adminSupportMaster[values.supportType] === adminSupportMaster.process_signed_applications
      ) {
        await dispatch(processSignedApplications());
        throw `Process Signed Applications Successfully`;
      }
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e));
      console.error((e as Error).message);
      if (typeof e === 'string') {
        setRaiseOpen(false);
        setCurrentPage(1);
        await fetchLogs();
      }
    }
  };

  const onSupportTypeChange = (supportType: any, values: any, setValues: any) => {
    if (adminSupportMaster[supportType] === adminSupportMaster.change_expired_status) {
      setValues({
        ...values,
        supportType: supportType,
        phone: '',
      });
      return;
    }

    if (adminSupportMaster[supportType] === adminSupportMaster.change_status_to_draft) {
      setValues({
        ...values,
        supportType: supportType,
        phone: '',
        status: '',
      });
      return;
    }

    setValues({
      ...values,
      supportType: supportType,
      application_number: '',
      status: '',
    });
  };

  const filterBadgeCount =
    (filters.supportTypes.length ? 1 : 0) + (filters.from || filters.to ? 1 : 0);

  const columns: ColumnType[] = [
    {
      header: 'Ticket ID',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (row: AdminSupportLog) => row.id,
    },
    {
      header: 'Mobile number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (row: AdminSupportLog) => formatMobile(row),
    },
    {
      header: 'Application Number',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (row: AdminSupportLog) => row.applicationNumber || 'N/A',
    },
    {
      header: 'Support Type',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (row: AdminSupportLog) => formatSupportType(row.supportType),
    },
    {
      header: 'Last Updated',
      align: 'left',
      headerAlign: 'left',
      valueGetter: (row: AdminSupportLog) => formatTicketTime(row.updatedAt || row.createdAt),
    },
  ];

  return (
    <CommonLayout>
      <>
      <Box sx={{ px: { xs: 2, sm: 0 }, pb: 3 }}>
        <Breadcrumbs
          separator=">"
          aria-label="breadcrumb"
          sx={{
            mb: 1.5,
            '& .MuiBreadcrumbs-separator': { mx: 0.75, color: '#6C737F' },
            '.MuiTypography-root, .MuiLink-root': {
              fontSize: 14,
              color: '#111927',
              fontFamily: 'Inter, sans-serif',
            },
          }}>
          <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
            Home
          </Link>
          <Typography sx={{ fontWeight: 500, color: '#6C737F !important' }}>Admin Support</Typography>
        </Breadcrumbs>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
            mb: 2.5,
          }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: { xs: 20, sm: 24 },
              fontWeight: 700,
              color: '#092056',
            }}>
            Admin Support
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setRaiseOpen(true)}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              textTransform: 'none',
              bgcolor: '#2057A6',
              borderRadius: '8px',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#1A4A8C', boxShadow: 'none' },
            }}>
            Raise New Query
          </Button>
        </Box>

        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #F2F4F7',
            boxShadow: '0px 4px 16px rgba(26, 43, 60, 0.04)',
            p: { xs: 2, sm: 2.5 },
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              mb: 2.5,
            }}>
            <TextField
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search by Ticket ID"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchSharpIcon sx={{ color: '#9CA3AF', fontSize: 20 }} aria-hidden />
                  </InputAdornment>
                ),
              }}
              sx={{
                flex: 1,
                minWidth: { xs: '100%', sm: 260 },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14,
                  '& fieldset': { borderColor: '#E5E7EB' },
                },
              }}
            />
            <Button
              id="filter-icon-button"
              aria-label={
                filterBadgeCount > 0
                  ? `Filters, ${filterBadgeCount} applied`
                  : 'Filters'
              }
              aria-controls="filter-menu"
              aria-haspopup="true"
              aria-expanded={filtersOpen ? 'true' : undefined}
              onClick={() => setFiltersOpen(true)}
              startIcon={
                <Badge
                  badgeContent={filterBadgeCount}
                  color="info"
                  sx={{ '& .MuiBadge-badge': { fontSize: 10 } }}>
                  <FilterAltOutlinedIcon sx={{ fontSize: 24, color: '#6B7280' }} aria-hidden />
                </Badge>
              }
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 14,
                fontWeight: 500,
                textTransform: 'none',
                color: '#6B7280',
              }}>
              Filters
            </Button>
            <AdminSupportFilterDrawer
              filters={filters}
              open={filtersOpen}
              handleClose={() => setFiltersOpen(false)}
              onFiltersChange={(value) => {
                setFilters(value);
                setCurrentPage(1);
              }}
            />
          </Box>

          <DataTable
            tableData={logs}
            tableHeader={columns}
            isLoading={isLoading}
            currentPage={undefined}
            noOfPages={1}
            renderAdditionalRow={false}
            tableBodyCustomStyles={userMgmtTableBodyStyles}
            rowCustomStyles={userMgmtRowStyles}
            tableHeaderCustomStyles={userMgmtTableHeaderStyles}
            boxStyles={userMgmtBoxStyles}
          />

          {total > 0 && (
            <ListPaginationBar
              pageCount={noOfPages}
              page={currentPage}
              onPageChange={(_e: ChangeEvent<unknown>, value: number) => setCurrentPage(value)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(rows) => {
                setRowsPerPage(rows);
                setCurrentPage(1);
              }}
              total={total}
            />
          )}
        </Box>
      </Box>

      <Dialog
        open={raiseOpen}
        onClose={() => setRaiseOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '16px', p: { xs: 1, sm: 2 } } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 18,
              fontWeight: 700,
              color: '#092056',
            }}>
            Raise New Query
          </Typography>
          <IconButton onClick={() => setRaiseOpen(false)} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        <Formik
          initialValues={adminSupportInitialValues}
          onSubmit={handleSubmit}
          validate={(values: adminSupportValues) => {
            try {
              validateYupSchema(values, adminSupportValidation, true, values);
            } catch (e) {
              return yupToFormErrors(e);
            }
          }}
          enableReinitialize={true}>
          {({ handleSubmit, values, setValues }) => (
            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ p: 1 }}>
              <Grid container rowSpacing={1} columnSpacing={3}>
                <Grid item xs={12}>
                  <RequiredFieldsMessage />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MFSelectField
                    name={`supportType`}
                    label={'Support Type *'}
                    items={supportOptions.map((item) => ({
                      key: adminSupportMaster[item],
                      value: item,
                    }))}
                    onChange={(e) => {
                      onSupportTypeChange(e.target.value, values, setValues);
                    }}
                  />
                </Grid>
                {![
                  adminSupportMaster.change_expired_status,
                  adminSupportMaster.change_status_to_draft,
                  adminSupportMaster.process_signed_applications,
                ].includes(adminSupportMaster[values.supportType]) && (
                  <Grid item xs={12} sm={6}>
                    <MFTextField
                      name={`phone`}
                      label={`Mobile Number *`}
                      placeholder="Enter Mobile Number"
                      onKeyDown={(e) => {
                        preventSpecialCharacters(e);
                      }}
                      startAdornment={
                        <CountryCodesDropDown name={'countryCode'} value={values.countryCode} />
                      }
                    />
                  </Grid>
                )}
                {[
                  adminSupportMaster.change_expired_status,
                  adminSupportMaster.change_status_to_draft,
                ].includes(adminSupportMaster[values.supportType]) && (
                  <Grid item xs={12} sm={6}>
                    <MFTextField
                      name={`application_number`}
                      label={`Application Number *`}
                      placeholder="Enter Application Number"
                    />
                  </Grid>
                )}
                {adminSupportMaster[values.supportType] ===
                  adminSupportMaster.change_expired_status && (
                  <Grid item xs={12} sm={6}>
                    <MFSelectField
                      name={`status`}
                      label={'Status'}
                      items={Object.keys(SUPPORT_STATUS).map((item) => ({
                        key: SUPPORT_STATUS[item],
                        value: item,
                      }))}
                    />
                  </Grid>
                )}
              </Grid>
              <Box sx={{ width: '100%', maxWidth: 320, mx: 'auto', mt: 3, mb: 1 }}>
                <MFSubmitButton label="Submit" />
              </Box>
            </Box>
          )}
        </Formik>
      </Dialog>
      </>
    </CommonLayout>
  );
}
